/**
 * TenantSwitcher — dropdown "Przełącz konto" w TenantTopbar (ADR-0075).
 *
 * Multitenant "wspólne konto": gdy centralne konto ma dostęp do >1 firmy, pokazujemy
 * przełącznik. Klik na firmę → `switchTenant()` (POST /auth/switch-tenant → handoff →
 * redirect na subdomenę firmy, bez ponownego logowania). Plakietka "Administrator"
 * gdy rola == admin (parytet z ekranem "Wybierz konto").
 *
 * Renderujemy TYLKO gdy availableTenants.length > 1 (jedna firma = brak sensu).
 * Wzorzec dropdownu 1:1 z CompanySwitcher (click-outside, listbox a11y).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronDown, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/Button';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { isTenantUpdatingError } from '@/lib/tenant-updating';
import { cn } from '@/lib/utils';

export function TenantSwitcher() {
  const { t } = useTranslation();
  const { me, switchTenant } = useTenantAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const tenants = useMemo(() => me?.availableTenants ?? [], [me?.availableTenants]);
  const current = useMemo(() => tenants.find((tn) => tn.isCurrent) ?? null, [tenants]);

  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Sens tylko przy >1 firmie centralnego konta.
  if (!me || tenants.length <= 1) return null;

  async function handleSelect(tenantId: string, isCurrent: boolean) {
    if (isCurrent || switching) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await switchTenant(tenantId);
      // switchTenant przekierowuje (window.location) — nie zamykamy dropdownu.
    } catch (err) {
      setSwitching(false);
      setOpen(false);
      // ADR-0113b §5: baza wybranej firmy w aktualizacji — komunikat zamiast ogólnego błędu.
      toast.show({
        variant: 'error',
        title: isTenantUpdatingError(err)
          ? t('tenantUpdating.loginError')
          : t('tenant.tenantSwitcher.error'),
      });
    }
  }

  return (
    <div className="relative" ref={dropdownRef} data-testid="tenant-switcher">
      <Button
        variant="secondary"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={switching}
        className="h-auto gap-2 rounded-lg bg-bg-elevated px-3 py-1.5 hover:border-accent-300 hover:bg-bg-elevated"
        data-testid="tenant-switcher-toggle"
      >
        <div className="flex h-6 w-6 items-center justify-center rounded bg-bg-secondary text-text-muted">
          <Building2 size={14} aria-hidden="true" />
        </div>
        <span className="max-w-[180px] truncate text-sm font-medium text-text-primary">
          {current?.name ?? t('tenant.tenantSwitcher.selectPlaceholder')}
        </span>
        <ChevronDown size={14} aria-hidden="true" />
      </Button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('tenant.tenantSwitcher.listLabel')}
          className="absolute right-0 z-20 mt-1 max-h-80 w-72 overflow-y-auto rounded-lg border border-border-secondary bg-bg-elevated shadow-lg"
          data-testid="tenant-switcher-list"
        >
          <li className="border-b border-border-secondary px-3 py-2 text-xs font-medium text-text-muted">
            {t('tenant.tenantSwitcher.header')}
          </li>
          {tenants.map((tn) => (
            <li key={tn.tenantId} role="option" aria-selected={tn.isCurrent}>
              <Button
                variant="ghost"
                onClick={() => handleSelect(tn.tenantId, tn.isCurrent)}
                disabled={switching || (tn.updating === true && !tn.isCurrent)}
                className={cn(
                  'h-auto w-full justify-start gap-2 rounded-none px-3 py-2 text-left text-sm font-normal hover:bg-bg-secondary',
                  tn.isCurrent && 'bg-bg-secondary',
                )}
                data-testid={`tenant-switcher-item-${tn.slug}`}
              >
                <div className="flex h-6 w-6 items-center justify-center rounded bg-bg-secondary text-text-muted">
                  <Building2 size={14} aria-hidden="true" />
                </div>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-text-primary">{tn.name}</span>
                  {tn.isAdmin && (
                    <span className="flex items-center gap-1 text-xs text-text-muted">
                      <ShieldCheck size={11} aria-hidden="true" />
                      {t('tenant.tenantSwitcher.adminRole')}
                    </span>
                  )}
                  {tn.updating && (
                    <span className="text-xs text-warning-700">
                      {t('tenantUpdating.companyBadge')}
                    </span>
                  )}
                </span>
                {tn.isCurrent && <Check size={14} className="text-accent-600" aria-hidden="true" />}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
